import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  Avatar,
  Button,
  CopyableField,
  DateTimeTz,
  LoadingIcon,
} from "@web-app/components";
import { DoordashResponse } from "@web-app/types";
import { timeZoneMap } from "@web-app/utils/helpers";
import http from "@web-app/utils/http";

type DoordashDeliveryPropsType = {
  orderId: string;
  timezone: keyof typeof timeZoneMap;
};

const deliverableActionMap: { [key: string]: string } = {
  contactless_dropoff: "Contactless Dropoff",
  normal: "Normal",
};

const undeliverableStatusMap: { [key: string]: string } = {
  dispose: "Dispose",
  return_to_pickup: "Return to pickup",
};

export default function DoordashDeliveryInfoV2({
  orderId,
  timezone,
}: DoordashDeliveryPropsType) {
  const [data, setData] = useState<DoordashResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getDelivery = useCallback(async () => {
    const resp = await http.get(`/integrations/doordash/deliveries/${orderId}`);

    setData(resp.data.data);
    setIsLoading(false);
  }, [orderId]);

  const cancelDelivery = useCallback(async () => {
    const resp = await http.put(
      `/integrations/doordash/deliveries/${orderId}/cancel`,
    );
    if (resp.status === 200) {
      toast.success("Delivery canceled successfully");
      setData(null);
      setIsLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    getDelivery();
  }, [getDelivery]);

  const getExternalDeliveryFee = () => {
    return (data?.fee ?? 0) / 100;
  };

  const getExternalDeliveryDuration = () => {
    const pickupEta = data?.pickup_time_estimated;
    const dropoffEta = data?.dropoff_time_estimated;
    if (pickupEta && dropoffEta) {
      return dayjs(dropoffEta).diff(dayjs(pickupEta), "minute");
    }
  };

  const getDriverName = () => {
    if (data?.dasher_name) {
      return data.dasher_name;
    }
    return "";
  };

  const getDriverPhoneNumber = () => {
    if (data?.dasher_dropoff_phone_number) {
      return data.dasher_dropoff_phone_number;
    }
    return "";
  };

  const canCancelDelivery = () => {
    return !data?.dasher_id; // Can cancel if no dasher is assigned
  };

  const onRefresh = () => {
    setIsLoading(true);
    getDelivery();
  };

  const onCancel = () => {
    setIsLoading(true);
    cancelDelivery();
  };

  return (
    <div>
      {isLoading ? (
        <LoadingIcon className="w-8 h-8" />
      ) : (
        <div>
          {data === null ? (
            <>Not available</>
          ) : (
            <div>
              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Cost</span>
                  <span className="tw-details-field-value !text-xs font-semibold">
                    {`$${getExternalDeliveryFee()}`}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Duration</span>
                  <span className="tw-details-field-value flex items-center gap-2">
                    {`${getExternalDeliveryDuration() || "N/A"} minutes`}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Pickup ETA</span>
                  <span className="tw-details-field-value">
                    <DateTimeTz
                      start={new Date(data?.pickup_time_estimated)}
                      timezone={timezone}
                      presentation="default"
                    />
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Dropoff ETA</span>
                  <span className="tw-details-field-value">
                    <DateTimeTz
                      start={new Date(data?.dropoff_time_estimated)}
                      timezone={timezone}
                      presentation="default"
                    />
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">
                    Deliverable Action
                  </span>
                  <span className="tw-details-field-value">
                    {data?.contactless_dropoff
                      ? deliverableActionMap["contactless_dropoff"]
                      : deliverableActionMap["normal"] || "N/A"}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">
                    Undeliverable Action
                  </span>
                  <span className="tw-details-field-value">
                    {undeliverableStatusMap[
                      data?.action_if_undeliverable || ""
                    ] || "N/A"}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title flex items-center">
                    Assignee
                  </span>
                  <span className="flex items-center gap-2">
                    {!getDriverName() && (
                      <span className="tw-details-field-value">
                        No Assignee
                      </span>
                    )}
                    {getDriverName() && (
                      <>
                        <span>
                          <Avatar image={undefined} size={10} />
                        </span>
                        <span className="flex flex-col">
                          <span className="tw-details-field-value !font-semibold break-words">
                            {getDriverName()}
                          </span>
                          <CopyableField
                            icon="fasPhone"
                            value={getDriverPhoneNumber()}
                            appearance="inline"
                            className="text-xs font-normal"
                          />
                        </span>
                      </>
                    )}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title flex items-center">
                    Tracking Link
                  </span>
                  <span
                    className="tw-details-field-value"
                    style={{ overflowWrap: "anywhere" }}
                  >
                    <CopyableField
                      value={data?.tracking_url}
                      appearance="link-inline"
                      className="text-xs font-normal"
                    />
                  </span>
                </div>
              </div>
              <div className="flex justify-end space-x-2 pr-4 pt-2">
                <Button plain size="sm" onClick={onRefresh}>
                  Refresh
                </Button>
                <Button
                  color="red"
                  size="sm"
                  disabled={!canCancelDelivery()}
                  onClick={onCancel}
                >
                  Cancel Delivery
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
